import { ALL_POSITIONS, PlayerPosition, RangeData, RangeDataset, RangeFile } from './types';
import { validateRangeFile } from './range-validation';

export type RangeFileResolver = (relativePath: string) => Promise<RangeFile | null>;

function ensureTrailingSlash(path: string): string {
   if (!path) {
      return '';
   }
   return path.endsWith('/') ? path : `${path}/`;
}

function extractSimpleHandCounters(file: RangeFile, index: number, label: string): RangeData {
   const entry = file.players_info[index];
   if (!entry || !entry.simple_hand_counters) {
      throw new Error(`Missing players_info[${index}] in ${label}`);
   }
   return entry.simple_hand_counters;
}

function followingPositions(position: PlayerPosition): PlayerPosition[] {
   const startIndex = ALL_POSITIONS.indexOf(position);
   return ALL_POSITIONS.filter((_, idx) => idx > startIndex);
}

export async function loadRangeDataset(resolver: RangeFileResolver): Promise<RangeDataset> {
   const dataset: RangeDataset = {
      ranges: {},
      raiseRanges: {},
      fourBetRanges: {},
   };

   async function loadFile(relativePath: string, options?: { optional?: boolean; playerIndex?: number }): Promise<RangeData | null> {
      const optional = options?.optional ?? false;
      const playerIndex = options?.playerIndex ?? 0;
      try {
         const file = await resolver(relativePath);
         if (!file) {
            if (!optional) {
               throw new Error(`Missing range file: ${relativePath}`);
            }
            return null;
         }
         return extractSimpleHandCounters(file, playerIndex, relativePath);
      } catch (error) {
         if (optional) {
            console.warn(`Optional range file skipped (${relativePath}):`, error);
            return null;
         }
         throw error;
      }
   }

   await Promise.all(
      ALL_POSITIONS.map(async position => {
         const path = `${position.toLowerCase()}.json`;
         const counters = await loadFile(path);
         if (!counters) {
            throw new Error(`Failed to load base range for ${position}`);
         }
         dataset.ranges[position] = counters;
      })
   );

   await Promise.all(
      ALL_POSITIONS.map(async raisePos => {
         const responderPositions = followingPositions(raisePos);
         await Promise.all(
            responderPositions.map(async responder => {
               const relativePath = `${raisePos.toLowerCase()}-raise/${responder.toLowerCase()}.json`;
               const counters = await loadFile(relativePath, { optional: true, playerIndex: 1 });
               if (!counters) {
                  return;
               }
               if (!dataset.raiseRanges[raisePos]) {
                  dataset.raiseRanges[raisePos] = {};
               }
               dataset.raiseRanges[raisePos][responder] = counters;
            })
         );
      })
   );

   await Promise.all(
      ALL_POSITIONS.map(async raisePos => {
         const responderPositions = followingPositions(raisePos);
         await Promise.all(
            responderPositions.map(async responder => {
               const relativePath = `${raisePos.toLowerCase()}-raise/${responder.toLowerCase()}_4bet.json`;
               const counters = await loadFile(relativePath, { optional: true, playerIndex: 1 });
               if (!counters) {
                  return;
               }
               if (!dataset.fourBetRanges[raisePos]) {
                  dataset.fourBetRanges[raisePos] = {};
               }
               dataset.fourBetRanges[raisePos][responder] = counters;
            })
         );
      })
   );

   return dataset;
}

export function createHostedRangeResolver(baseUrl: string): RangeFileResolver {
   const normalizedBase = ensureTrailingSlash(baseUrl.trim());
   if (!normalizedBase) {
      throw new Error('Range data URL is empty');
   }

   return async (relativePath: string) => {
      const target = `${normalizedBase}${relativePath}`;
      const response = await fetch(target);
      if (!response.ok) {
         if (response.status === 404) {
            return null;
         }
         throw new Error(`Failed to load range data from ${target}: ${response.status} ${response.statusText}`);
      }

      const json = await response.json();
      const requiredPlayers = getRequiredPlayersForPath(normalizeRelativePath(relativePath));
      return validateRangeFile(relativePath, json, { requiredPlayers });
   };
}

export function createMapRangeResolver(fileMap: Map<string, RangeFile>): RangeFileResolver {
   return async (relativePath: string) => {
      const normalized = normalizeRelativePath(relativePath);
      return fileMap.get(normalized) ?? null;
   };
}

function normalizeRelativePath(path: string): string {
   return path.replace(/\\/g, '/').replace(/^\.\/+/, '').replace(/^\/+/, '').toLowerCase();
}

function getRequiredPlayersForPath(path: string): number[] {
   return path.includes('-raise/') ? [1] : [0];
}

export function buildFileMapFromRecords(records: Array<{ path: string; data: unknown }>, label: string): Map<string, RangeFile> {
   const map = new Map<string, RangeFile>();
   records.forEach(record => {
      const normalized = normalizeRelativePath(record.path);
      const requiredPlayers = getRequiredPlayersForPath(normalized);
      const parsed = validateRangeFile(`${label}:${record.path}`, record.data, { requiredPlayers });
      map.set(normalized, parsed);

      const jsonSegmentIndex = normalized.indexOf('json/');
      if (jsonSegmentIndex >= 0) {
         const trimmed = normalized.slice(jsonSegmentIndex + 5);
         if (trimmed) {
            map.set(trimmed, parsed);
         }
      }
   });
   return map;
}
